// @flow

import type { Endpoints, EntityFieldCollection, SortParams } from '../types';
import { createdAtField, idField, paymentModeField } from '../abstract/StoredEntity';
import type { StoredEntity } from '../abstract/StoredEntity';
import { constructEndpoints } from '../helpers';
import User from './User';
import OrgLocation from './OrgLocation';
import { DescendingSortOrder } from '../types';

class LocationSession implements StoredEntity {
    static classInternalName = 'location_session';

    static fields: EntityFieldCollection = {
        id: idField,
        createdAt: createdAtField,
        userId: {
            displayName: 'User',
            recordName: 'user_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => User,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        locationId: {
            displayName: 'Location',
            recordName: 'location_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => OrgLocation,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        firstCheckInTime: {
            displayName: 'First Check In',
            recordName: 'first_check_in_time',
            dataType: {
                primitive: 'ts',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        lastCheckInTime: {
            displayName: 'Last Check In',
            recordName: 'last_check_in_time',
            dataType: {
                primitive: 'ts',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        lastCheckOutTime: {
            displayName: 'Last Check Out',
            recordName: 'last_check_out_time',
            dataType: {
                primitive: 'ts',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: true,
        },
        expiration: {
            displayName: 'Expiration',
            recordName: 'expiration',
            dataType: {
                primitive: 'ts',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        invalidated: {
            displayName: 'Invalidated',
            recordName: 'invalidated',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        otc: {
            displayName: 'OTC',
            recordName: 'otc',
            dataType: {
                primitive: 'num',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        paymentMode: paymentModeField,
    };

    static fileFields: EntityFieldCollection = {};

    static defaultSort: SortParams = {
        field: LocationSession.fields.expiration,
        fieldName: 'expiration',
        order: DescendingSortOrder,
    };

    get id() {
        return this.record[LocationSession.fields.id.recordName];
    }

    get displayName() {
        return this.record[LocationSession.fields.id.recordName];
    }

    static endpoints: Endpoints = constructEndpoints('location-sessions');

    constructor(record: any) {
        this.record = record;
    }
}

export default LocationSession;
